import i18n from '@/i18n/config';
import type { UserListItem } from '@/types';

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';

import { apiClient } from '@/utils/api';

export interface EditForm {
  first_name: string;
  last_name: string;
  email: string;
  ai_daily_request_limit: string;
  plan_slug: 'starter' | 'pro' | 'enterprise';
}

export function useUserEdit() {
  const queryClient = useQueryClient();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onOpenChange: onEditOpenChange } = useDisclosure();
  const [editingUserId, setEditingUserId] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [editForm, setEditForm] = React.useState<EditForm>({
    first_name: '',
    last_name: '',
    email: '',
    ai_daily_request_limit: '',
    plan_slug: 'pro'
  });

  const openEdit = React.useCallback(
    (user: UserListItem) => {
      setEditingUserId(String(user.id));
      setEditForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        ai_daily_request_limit:
          user.ai_daily_request_limit === null || user.ai_daily_request_limit === undefined
            ? ''
            : String(user.ai_daily_request_limit),
        plan_slug: user.plan_slug ?? 'pro'
      });
      onEditOpen();
    },
    [onEditOpen]
  );

  const saveEdit = React.useCallback(async () => {
    if (!editingUserId) return;

    try {
      setIsSaving(true);
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        ai_daily_request_limit:
          editForm.ai_daily_request_limit.trim() === '' ? null : Number(editForm.ai_daily_request_limit),
        plan_slug: editForm.plan_slug
      };

      const { data } = await apiClient.put(`/users/${editingUserId}`, payload);

      // Invalidate and refetch users list to get fresh data
      await queryClient.invalidateQueries({ queryKey: ['users'] });

      // Also refetch the specific user query if it exists
      await queryClient.invalidateQueries({ queryKey: ['user', editingUserId] });

      // Show success message
      toast.success(i18n.t('copy.user_updated_successfully'));

      setIsSaving(false);
      onEditOpenChange();
      setEditingUserId(null);
    } catch (error) {
      setIsSaving(false);
      // Error is handled by axios interceptor
    }
  }, [editingUserId, editForm, onEditOpenChange, queryClient]);

  return {
    // State
    isEditOpen,
    editingUserId,
    isSaving,
    editForm,
    setEditForm,

    // Actions
    openEdit,
    saveEdit,
    onEditOpenChange
  };
}

